import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import SheinOpsListToolbar from '@/shein-platform/components/SheinOpsListToolbar';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import SheinOpsCopyHoverMenu from '@/shein-platform/components/SheinOpsCopyHoverMenu';
import SheinTemplateScopeTabs from '@/shein-platform/components/SheinTemplateScopeTabs';
import SheinStyleNoBatchFilter from '@/shein-platform/components/SheinStyleNoBatchFilter';
import SizeChartTemplateCreateModal from '@/shein-platform/modules/templates/SizeChartTemplateCreateModal';
import useMaterialTemplatePortableActions from '@/shein-platform/modules/templates/useMaterialTemplatePortableActions';
import { formatBeijingDateTime } from '@/shein-platform/utils/formatBeijingDateTime';
import { copyTemplateName } from '@/shein-platform/utils/sheinTemplateCopyName';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import { MATERIAL_TEMPLATE_TYPES } from '@/shein-platform/utils/sheinMaterialTemplatePortable';
import {
  buildSheinWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  resolveCopySourceScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  rowCanDeleteScopedTemplate,
  rowCanEditScopedTemplate,
  templateCopiedToast,
} from '@/shein-platform/constants/sheinTemplateScope';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/shein-platform/utils/sheinTemplateVersionGuard';
import useSheinTemplateScope from '@/shein-platform/hooks/useSheinTemplateScope';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { usePlatformShopPick } from '@/hooks/usePlatformShopPick';
import useSheinTemplateWriteShop from '@/shein-platform/hooks/useSheinTemplateWriteShop';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function rowKey(row) {
  return String(row?.id ?? '');
}

const COL_SPAN_FULL = 10;
const COL_SPAN_OFFICIAL = 9;

/** 模板库 · 尺码库（对齐证书库列表布局） */
export default function SizeChartTemplatePage() {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [] } = useSheinPageShopScope();
  const {
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
    categoryOptions,
    categories,
    shopsForOfficialTemplate,
    shopsForOfficialTemplates,
  } = useSheinTemplateScope({ preferredShopId: pageShopScope });
  

  
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const { ensureWriteShopId, ensureCopyShopId } = useSheinTemplateWriteShop({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    isOfficialTab,
    shopsForOfficialTemplate,
    shopsForOfficialTemplates,
  });

const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [styleNoDraft, setStyleNoDraft] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingShopId, setEditingShopId] = useState('');
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [copyingId, setCopyingId] = useState(null);
  const [batchCopying, setBatchCopying] = useState(false);
  const [moving, setMoving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const modalShopId = editingShopId || listShopId;
  const tableColSpan = isOfficialTab ? COL_SPAN_OFFICIAL : COL_SPAN_FULL;

  const load = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setItems([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await sheinPlatformAPI.listSizeChartTemplates(listShopId, {
        scope: scopeTab,
        keyword: keyword || undefined,
        styleNo: styleNo || undefined,
        productTypeId: productTypeId || undefined,
        page,
        pageSize,
        ...(listAllShops ? { allShops: true } : {}),
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total) || 0);
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('productTemplate.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId, page, pageSize, queryNonce, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedKeys.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
  }, [items, selectedKeys]);

  const selectableKeys = useMemo(
    () => items
      .filter((row) => rowCanDeleteScopedTemplate(row) || rowCanCopyScopedTemplate(row))
      .map(rowKey)
      .filter(Boolean),
    [items],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCategoryChange = (id) => {
    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : '';
    setCategoryIdDraft(id ? String(id) : '');
    setProductTypeIdDraft(nextTypeId);
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setStyleNo(styleNoDraft.trim());
    setProductTypeId(productTypeIdDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setStyleNoDraft('');
    setStyleNo('');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setProductTypeId('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resolveSelectedRows = () => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  };

  const resolvePageRows = useCallback(() => items, [items]);

  const resolveSelectedRowsForExport = useCallback(() => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  }, [selectedKeys]);

  const resolveExportRows = useCallback(() => {
    const selected = resolveSelectedRowsForExport();
    return selected.length ? selected : items;
  }, [resolveSelectedRowsForExport, items]);

  const fetchFilteredRows = useCallback(async () => {
    if (!canQuery || !listShopId) return [];
    return fetchAllPages(async (pageNo, size) => {
      const res = await sheinPlatformAPI.listSizeChartTemplates(listShopId, {
        scope: scopeTab,
        keyword: keyword || undefined,
        styleNo: styleNo || undefined,
        productTypeId: productTypeId || undefined,
        page: pageNo,
        pageSize: size,
        ...(listAllShops ? { allShops: true } : {}),
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId]);

  const {
    porting,
    onExportFilteredCsv,
    onExportPageCsv,
    onExportSelectedCsv,
    onExportJson,
    onImportJson,
  } = useMaterialTemplatePortableActions({
    type: MATERIAL_TEMPLATE_TYPES.sizeCharts,
    shopId: listShopId,
    resolveShopId: () => ensureWriteShopId({
      title: t('productTemplate.importPickTitle'),
      description: t('productTemplate.importPickDesc'),
      confirmText: t('productTemplate.importHere'),
    }),
    filePrefix: t('sizeChart.filePrefix'),
    resolveExportRows,
    resolvePageRows,
    resolveSelectedRows: resolveSelectedRowsForExport,
    fetchFilteredRows,
    buildCsvRows: (rows) => rows.map((row) => ({
      [t('productTemplate.colPriority')]: row.sortOrder ?? 0,
      [t('productTemplate.colStyleNo')]: row.styleNo || '',
      [t('productTemplate.colName')]: row.name || '',
      [t('productTemplate.colCategory')]: row.categoryPathLabel || row.productTypeId || '',
      [t('productTemplate.colAutoRule')]: row.autoRuleEnabled ? t('productTemplate.on') : t('productTemplate.off'),
      [t('sizeChart.specRows')]: row.sizeRowCount ?? '',
      [t('sizeChart.sizeAttrs')]: row.measureAttrCount ?? '',
      [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.updatedAt),
    })),
    getOne: (id) => sheinPlatformAPI.getSizeChartTemplate(listShopId, id, { scope: scopeTab }),
    listByProductType: async (productTypeId, ctx) => {
      const sid = ctx?.shopId || listShopId;
      const res = await sheinPlatformAPI.listSizeChartTemplates(sid, {
        scope: scopeTab,
        productTypeId: productTypeId || undefined,
        page: 1,
        pageSize: 200,
      });
      return Array.isArray(res?.items) ? res.items : [];
    },
    createOne: (body, ctx) => sheinPlatformAPI.createSizeChartTemplate(ctx?.shopId || listShopId, {
      ...body,
      ...(body.scope ? {} : { scope: scopeTab }),
    }),
    onImported: async () => {
      setSelectedKeys(new Set());
      setPage(1);
      setQueryNonce((n) => n + 1);
    },
  });

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdDelete'), 'error');
      return;
    }
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('sizeChart.batchDeleteMsg', { count: ids.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteSizeChartTemplates(listShopId, ids);
      const deleted = Number(res?.deleted);
      showToast(t('productTemplate.deletedCount', { count: Number.isFinite(deleted) ? deleted : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureCopyShopId(resolveSelectedRows());
    if (!writeShopId) return;
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    setBatchCopying(true);
    try {
      const res = await sheinPlatformAPI.batchCopySizeChartTemplates(writeShopId, {
        ids,
        sourceScope: scopeTab,
        targetScope,
      });
      const copied = Number(res?.copied);
      showToast(t('productTemplate.copiedCount', { count: Number.isFinite(copied) ? copied : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchCopyFailed'), 'error');
    } finally {
      setBatchCopying(false);
    }
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdMove'), 'error');
      return;
    }
    setMoving(true);
    try {
      const res = await sheinPlatformAPI.moveSizeChartTemplates(listShopId, {
        ids,
        targetScope,
      });
      const moved = Number(res?.moved);
      showToast(t('productTemplate.movedCount', { count: Number.isFinite(moved) ? moved : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  const onDeleteOne = async (row) => {
    if (!canQuery || !row?.id || !rowCanDeleteScopedTemplate(row)) return;
    const ok = await showSheinConfirm({
      title: t('productTemplate.deleteTitle'),
      message: t('productTemplate.deleteOne', { name: row.name || row.id }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await sheinPlatformAPI.deleteSizeChartTemplate(resolveTemplateRowShopId(row, listShopId), row.id);
      showToast(t('productTemplate.deleted'), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onCopyOne = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId) return;
    const writeShopId = await ensureCopyShopId(row);
    if (!writeShopId) return;
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await sheinPlatformAPI.copySizeChartTemplate(writeShopId, {
        sourceId: row.id,
        sourceScope: resolveCopySourceScope(row, { isOfficialTab, scopeTab }),
        targetScope: resolvedTarget,
        name: copyTemplateName(row.name),
      });
      showToast(templateCopiedToast(resolvedTarget), 'success');
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onToggleAutoRule = async (row, enabled) => {
    if (!canQuery || !row?.id || !rowCanEditScopedTemplate(row)) return;
    const id = row.id;
    const key = String(id);
    const prevEnabled = row.autoRuleEnabled === true || row.autoRuleEnabled === 1;
    setTogglingIds((prev) => new Set(prev).add(key));
    setItems((prev) => prev.map((item) => (
      String(item.id) === key ? { ...item, autoRuleEnabled: enabled } : item
    )));
    try {
      const body = { autoRuleEnabled: enabled };
      const version = parseTemplateVersion(row.version);
      if (version != null) body.version = version;
      const res = await sheinPlatformAPI.updateSizeChartTemplate(resolveTemplateRowShopId(row, listShopId), id, body);
      const nextVersion = parseTemplateVersion(res?.item?.version);
      setItems((prev) => prev.map((item) => (
        String(item.id) === key
          ? {
            ...item,
            autoRuleEnabled: enabled,
            ...(nextVersion != null ? { version: nextVersion } : {}),
          }
          : item
      )));
      showToast(enabled ? t('productTemplate.autoOn') : t('productTemplate.autoOff'), 'success');
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        String(item.id) === key ? { ...item, autoRuleEnabled: prevEnabled } : item
      )));
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'sizeChart' }]), 'warning', 3000);
        try {
          const res = await sheinPlatformAPI.getSizeChartTemplate(resolveTemplateRowShopId(row, listShopId), id, { scope: row.scope || scopeTab });
          const item = res?.item || res || {};
          const serverEnabled = item.autoRuleEnabled === true || item.autoRuleEnabled === 1;
          const serverVersion = parseTemplateVersion(item.version);
          setItems((prev) => prev.map((itemRow) => (
            String(itemRow.id) === key
              ? {
                ...itemRow,
                autoRuleEnabled: serverEnabled,
                ...(serverVersion != null ? { version: serverVersion } : {}),
              }
              : itemRow
          )));
        } catch {
          // 单行刷新失败时保持回滚状态
        }
      } else {
        showToast(e?.message || t('productTemplate.autoUpdateFailed'), 'error');
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const emptyHint = !canQuery
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (keyword || styleNo || productTypeId
        ? t('sizeChart.noMatch')
        : (isOfficialTab ? t('sizeChart.emptyOfficial') : t('sizeChart.empty'))));

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || deleting || porting || batchCopying || moving}
            batchCopying={batchCopying}
            batchMoving={moving}
            batchOpsMenu={buildSheinWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || deleting || porting || isOfficialTab,
            })}
            exportLabel={opsExportLabel(porting)}
            exportDisabled={!canQuery || loading || porting}
            exportMenu={[
              ...buildOpsExportMenu({
                exporting: porting,
                filteredCount: total,
                pageCount: items.length,
                selectedCount: selectedKeys.size,
                onExportFiltered: onExportFilteredCsv,
                onExportPage: onExportPageCsv,
                onExportSelected: onExportSelectedCsv,
              }),
              {
                key: 'export-json',
                label: t('productTemplate.exportJson'),
                disabled: !canQuery || loading || porting,
                onClick: onExportJson,
              },
            ]}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'import-json',
                label: porting ? t('productTemplate.importing') : t('productTemplate.importJson'),
                disabled: !canQuery || loading || deleting || porting,
                onClick: onImportJson,
              },
              {
                key: 'create',
                label: t('sizeChart.create'),
                tone: 'primary',
                disabled: !canQuery || loading || deleting || porting,
                onClick: async () => {
                  const writeShopId = await ensureWriteShopId({
                    title: t('productTemplate.createPickTitle'),
                    description: t('sizeChart.createPickDesc'),
                    confirmText: t('productTemplate.createHere'),
                  });
                  if (!writeShopId) {
                    showToast(t('returnList.pickShop'), 'warning');
                    return;
                  }
                  setEditingId(null);
                  setEditingShopId(writeShopId);
                  setModalOpen(true);
                },
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--category">
              <span className="spo-field__label">{t('productTemplate.colCategory')}</span>
              <CategoryCascaderSelect
                categories={categories}
                value={categoryIdDraft}
                disabled={!canQuery || loading}
                placeholder={!canQuery ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                emptyText={!canQuery ? t('returnList.pickShop') : t('productTemplate.noCategories')}
                onChange={onCategoryChange}
              />
            </div>
            <SheinStyleNoBatchFilter
              value={styleNoDraft}
              disabled={!canQuery}
              onChange={setStyleNoDraft}
              onEnter={runQuery}
            />
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('productTemplate.keyword')}</span>
              <input
                className="spo-input"
                value={keywordDraft}
                disabled={!canQuery}
                placeholder={t('productTemplate.keywordPh')}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runQuery();
                }}
              />
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--ghost spo-btn--sm"
              disabled={!canQuery || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!canQuery || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard loading={Boolean(canQuery) && loading} loadingLabel={t('productTemplate.querying')}>
        {shopDimensionTabs}
        <SheinTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('sizeChart.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <div className="spo-drafts__table-wrap spo-certs-table-wrap">
          <table className={`spo-publish__table spo-drafts__table spo-certs-table spo-tpl-table spo-tpl-table--size${isOfficialTab ? ' spo-tpl-table--size-official' : ''}`}>
            <colgroup>
              <col className="spo-certs-col spo-certs-col--check" />
              <col className="spo-certs-col spo-certs-col--rest" />
              <col className="spo-certs-col spo-certs-col--style-no" />
              <col className="spo-certs-col spo-certs-col--name" />
              <col className="spo-certs-col spo-certs-col--category" />
              <col className="spo-certs-col spo-certs-col--rest" />
              <col className="spo-certs-col spo-certs-col--rest" />
              <col className="spo-certs-col spo-certs-col--rest" />
              {!isOfficialTab ? <col className="spo-certs-col spo-certs-col--rest" /> : null}
              <col className="spo-certs-col spo-certs-col--rest" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-certs-th spo-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="spo-certs-th spo-certs-th--order">{t('productTemplate.colPriority')}</th>
                <th scope="col" className="spo-certs-th">{t('productTemplate.colStyleNo')}</th>
                <th scope="col" className="spo-certs-th">{t('productTemplate.colName')}</th>
                <th scope="col" className="spo-certs-th">{t('productTemplate.colCategory')}</th>
                <th scope="col" className="spo-certs-th spo-certs-th--switch">{t('productTemplate.colAutoRule')}</th>
                <th scope="col" className="spo-certs-th">{t('sizeChart.specRows')}</th>
                <th scope="col" className="spo-certs-th">{t('sizeChart.sizeAttrs')}</th>
                {!isOfficialTab ? <th scope="col" className="spo-certs-th">{t('productTemplate.colUpdatedAt')}</th> : null}
                <th scope="col" className="spo-certs-th spo-certs-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || items.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                const canEdit = rowCanEditScopedTemplate(row);
                const canDelete = rowCanDeleteScopedTemplate(row);
                const canCopy = rowCanCopyScopedTemplate(row);
                const canSelect = canDelete || canCopy;
                const rowShopId = resolveTemplateRowShopId(row, listShopId);
                return (
                  <tr
                    key={key}
                    className={canSelect ? 'spo-row--clickable' : undefined}
                    onClick={(e) => {
                      if (!canSelect) return;
                      if (e.target.closest('button, a, input, .spo-copy-hover, .shein-draft-actions')) return;
                      toggleOne(key);
                    }}
                  >
                    <td className="spo-certs-td spo-certs-td--check">
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        disabled={!canSelect}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          if (!canSelect) return;
                          toggleOne(key);
                        }}
                      />
                    </td>
                    <td className="spo-certs-td spo-certs-td--order">{row.sortOrder ?? 0}</td>
                    <td className="spo-certs-td">{row.styleNo || '—'}</td>
                    <td className="spo-certs-td" title={row.name || ''}>
                      <strong>{row.name || '—'}</strong>
                      {row.remark ? <div className="spo-muted">{row.remark}</div> : null}
                    </td>
                    <td className="spo-certs-td">{row.categoryPathLabel || row.productTypeId || '—'}</td>
                    <td className="spo-certs-td spo-certs-td--switch">
                      <SheinSwitch
                        checked={!!row.autoRuleEnabled}
                        disabled={!canEdit || deleting || togglingIds.has(key)}
                        ariaLabel={t('productTemplate.autoRuleAria', { name: row.name || t('productTemplate.templateFallback') })}
                        onChange={(next) => onToggleAutoRule(row, next)}
                      />
                    </td>
                    <td className="spo-certs-td">{row.sizeRowCount ?? '—'}</td>
                    <td className="spo-certs-td">{row.measureAttrCount ?? '—'}</td>
                    {!isOfficialTab ? (
                      <td className="spo-certs-td spo-certs-td--time">{formatBeijingDateTime(row.updatedAt)}</td>
                    ) : null}
                    <td className="spo-certs-td spo-certs-td--ops">
                      <div className="shein-draft-actions">
                        {canEdit ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => {
                              setEditingId(row.id);
                              setEditingShopId(rowShopId);
                              setModalOpen(true);
                            }}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {canCopy ? (
                          <SheinOpsCopyHoverMenu
                            disabled={deleting || copyingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOne(row, target),
                            })}
                          />
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => onDeleteOne(row)}
                          >
                            {t('returnList.delete')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('sizeChart.pager')}
        />
      </ProductListCard>

      <SizeChartTemplateCreateModal
        open={modalOpen}
        shopId={modalShopId}
        templateId={editingId}
        templateScope={scopeTab}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setEditingShopId('');
        }}
        onSaved={() => {
          setPage(1);
          setQueryNonce((n) => n + 1);
        }}
      />
      {shopPickModal}
    </>
  );
}
